import { useSnackbarProgressStore } from '@aibees/shared/store';
import { useCallback, useMemo } from 'react';

/**
 * Hook for controlled progress snackbars (e.g., download progress).
 * Reads progress from Zustand store and provides formatted values.
 */
export function useControlledProgressSnackbar(snackbarId: string | number) {
  // Subscribe to progress updates from the Zustand store (automatic reactivity)
  const storeProgress = useSnackbarProgressStore((state) =>
    state.progress.get(snackbarId)
  );

  const progressValue = useMemo(() => {
    // Clamp to 0-100
    if (typeof storeProgress === 'number') {
      return Math.min(100, Math.max(0, storeProgress));
    }
    // Default to 0 if no progress yet
    return 0;
  }, [storeProgress]);

  const hasProgress = typeof storeProgress === 'number';

  /**
   * Formats a message with progress percentage appended
   */
  const formatMessageWithProgress = useCallback(
    (message: string): string => {
      if (hasProgress) {
        return `${message} (${progressValue}%)`;
      }
      return message;
    },
    [hasProgress, progressValue]
  );

  return {
    progress: progressValue,
    hasProgress,
    formatMessageWithProgress,
  };
}
